import React, { useState, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import "react-day-picker/style.css";
import { format } from 'date-fns';
import { enUS } from 'date-fns/locale';
import "./css/CustomDayPicker.css";

function parseToLocalDate(value) {
    if (!value) return null;

    if (value instanceof Date) {
        return new Date(value.getFullYear(), value.getMonth(), value.getDate());
    }

    if (typeof value === 'string') {
        const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/;
        if (isoDateOnly.test(value)) {
            const parts = value.split('-').map((p) => parseInt(p, 10));
            return new Date(parts[0], parts[1] - 1, parts[2]);
        }

        const tmp = new Date(value);
        if (!isNaN(tmp)) {
            return new Date(tmp.getFullYear(), tmp.getMonth(), tmp.getDate());
        }
    }

    return null;
}

function CustomOverlay({ label, isOpen, onClose, selectedDay, onDaySelect }) {
    if (!isOpen) {
        return null;
    }

    const customLocale = {
        ...enUS,
        localize: {
            ...enUS.localize,
            day: (n) => ['S', 'M', 'T', 'W', 'T', 'F', 'S'][n],
        },
    };

    const selectedDate = parseToLocalDate(selectedDay) || new Date();
    let options = { weekday: 'short', month: 'short', day: 'numeric' };

    return (
        <div
            style={{
                position: 'absolute',
                zIndex: 1000,
                backgroundColor: 'white',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                borderRadius: '0.25rem',
                marginLeft: '230px',
                marginTop: '-380px',
            }}
        >
            <div className="calendar-bg">
                <p className="calendar-txt ct-small-1">{label}</p>
                <p className="calendar-txt ct-small">{selectedDate ? selectedDate.getFullYear() : ''}</p>
                <p className="calendar-txt ct-big">
                    {selectedDate ? selectedDate.toLocaleDateString('en-US', options) : ''}
                </p>
            </div>
            <DayPicker
                mode="single"
                locale={customLocale}
                selected={selectedDate}
                onSelect={onDaySelect}
                defaultMonth={selectedDate || new Date()}
                modifiers={{ birthday: selectedDay }}
            />
            <div className="calendar-footer">
                <button className="calendar-button calendar-button-ok" type="button" onClick={() => onDaySelect(selectedDay || new Date())}>
                    OK
                </button>
                <button className="calendar-button calendar-button-cancel" type="button" onClick={onClose}>
                    CANCEL
                </button>
            </div>
        </div>
    );
}

export default function CustomDatePicker({ defValue, readOnly, onChange, label, id, overlayClass, className }) {
    const initialDate = parseToLocalDate(defValue);
    const [selectedDay, setSelectedDay] = useState(initialDate);
    const [inputValue, setInputValue] = useState(initialDate ? format(initialDate, 'yyyy-MM-dd', { locale: enUS }) : '');
    const [isOverlayOpen, setIsOverlayOpen] = useState(false);

    useEffect(() => {
        const handleGlobalOverlayClose = (event) => {
            if (event.detail !== id) {
                setIsOverlayOpen(false);
            }
        };

        window.addEventListener('closeOverlays', handleGlobalOverlayClose);
        return () => {
            window.removeEventListener('closeOverlays', handleGlobalOverlayClose);
        };
    }, [id]);

    useEffect(() => {
        if (defValue) {
            const d = parseToLocalDate(defValue);
            setSelectedDay(d);
            setInputValue(d ? format(d, 'yyyy-MM-dd', { locale: enUS }) : '');
        } else {
            setSelectedDay(null);
            setInputValue('');
        }
    }, [defValue]);

    const handleDaySelect = (day) => {
        const resolvedDay = day ?? selectedDay ?? new Date();
        const localDay = parseToLocalDate(resolvedDay);
        setSelectedDay(localDay);
        const dateOnly = localDay ? format(localDay, 'yyyy-MM-dd') : '';
        setInputValue(dateOnly);
        setIsOverlayOpen(false);
        if (onChange) {
            onChange(dateOnly);
        }
    };

    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);

        const parts = value.split('-');
        let parsedDate = null;
        if (parts.length === 3) {
            const y = parseInt(parts[0], 10);
            const m = parseInt(parts[1], 10);
            const d = parseInt(parts[2], 10);
            if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
                parsedDate = new Date(y, m - 1, d);
            }
        }

        if (parsedDate && !isNaN(parsedDate)) {
            setSelectedDay(parsedDate);
            if (onChange) {
                onChange(format(parsedDate, 'yyyy-MM-dd'));
            }
        } else {
            setSelectedDay(null);
            if (onChange) {
                onChange(null);
            }
        }
    };

    const toggleOverlay = () => {
        if (!isOverlayOpen) {
            window.dispatchEvent(new CustomEvent('closeOverlays', { detail: id }));
        }
        setIsOverlayOpen(!isOverlayOpen);
    };

    return (
        <div style={{ position: 'relative' }}>
            <input
                readOnly={readOnly}
                type="date"
                className={`date-control-div ${readOnly ? '' : 'input-editable'} ${className || ''}`}
                style={{
                    border: '1px solid #ced4da'
                }}
                value={inputValue}
                onChange={handleInputChange}
                onBlur={() => onChange && onChange(inputValue)}
                id={id}
            />

            <div
                id={`${id}-button`}
                className="input-group-addon"
                onClick={toggleOverlay}
            >
                <i className="fa fa-calendar"></i>
            </div>

            <CustomOverlay
                isOpen={isOverlayOpen}
                onClose={() => setIsOverlayOpen(false)}
                selectedDay={selectedDay}
                onDaySelect={handleDaySelect}
                label={label}
            />
        </div>
    );
}